import { Entity, World } from "ecsy";
import { ConstraintData } from "./DataComponents/ConstraintData";
import { LifeTimeData } from "./DataComponents/LifeTimeData";
import { MassData } from "./DataComponents/MassData";
import { RotationData } from "./DataComponents/RotationData";
import { TransformData } from "./DataComponents/TransformData";
import { VelocityData } from "./DataComponents/VelocityData";
import { Vector2 } from "./Math/Vector2";
import { BallRenderSystem } from "./Systems/BallRenderSystem";
import { ClearCanvasSystem } from "./Systems/ClearCanvasSystem";
import { ConstraintPhysicsSystem } from "./Systems/ConstraintPhysicsSystem";
import { DebugRenderSystem } from "./Systems/DebugRenderSystem";
import { DraggableHighlightRenderSystem } from "./Systems/DraggableHighlightRenderSystem";
import { DraggableSystem } from "./Systems/DraggableSystem";
import { FanPhysicsSystem } from "./Systems/FanPhysicsSystem";
import { FanRenderSystem } from "./Systems/FanRenderSystem";
import { GravitySystem } from "./Systems/GravitySystem";
import { LifeTimeSystem } from "./Systems/LifeTimeSystem";
import { RopeRenderSystem } from "./Systems/RopeRenderSystem";
import { SpawnBallSystem } from "./Systems/SpawnBallSystem";
import { VelocityMoveSystem } from "./Systems/VelocityMoveSystem";
import { BallTag } from "./TagComponents/BallTag";
import { DebugTag } from "./TagComponents/DebugTag";
import { DraggableHighlightTag } from "./TagComponents/DraggableHighlightTag";
import { DraggableTag } from "./TagComponents/DraggableTag";
import { FanTag } from "./TagComponents/FanTag";
import { RopeRootTag } from "./TagComponents/RopeRootTag";
import { RopeTag } from "./TagComponents/RopeTag";
import { SpawnBallTag } from "./TagComponents/SpawnBallTag";
import { Constraint } from "./Utils/Constraint";

export const init = (world: World) => {
  // Get the main canvas.
  const mainCanvas = document.getElementById("mainCanvas") as HTMLCanvasElement;

  // Resize the canvas to the style size.
  mainCanvas.width = mainCanvas.clientWidth;
  mainCanvas.height = mainCanvas.clientHeight;

  // Get rotation slider.
  const rotationSlider = document.getElementById(
    "rotationSlider"
  ) as HTMLInputElement;

  // Get create rope button.
  const createRopeButton = document.getElementById(
    "create1AnchorRope"
  ) as HTMLButtonElement;

  // Bind create rope button.
  createRopeButton.onclick = () => {
    create1AnchorRope(world, mainCanvas);
  };

  // Get create rope button.
  const create2AnchorRopeButton = document.getElementById(
    "create2AnchorRope"
  ) as HTMLButtonElement;

  // Bind create rope button.
  create2AnchorRopeButton.onclick = () => {
    create2AnchorRope(world, mainCanvas);
  };

  // Get create cloth button.
  const createClothButton = document.getElementById(
    "createCloth"
  ) as HTMLButtonElement;

  // Bind create cloth button.
  createClothButton.onclick = () => {
    createCloth(world, mainCanvas);
  };

  // Register TagComponents.
  world
    .registerComponent(FanTag)
    .registerComponent(BallTag)
    .registerComponent(SpawnBallTag)
    .registerComponent(RopeTag)
    .registerComponent(RopeRootTag)

    .registerComponent(DraggableTag)
    .registerComponent(DraggableHighlightTag)
    .registerComponent(DebugTag);

  // Register DataComponents.
  world
    .registerComponent(TransformData)
    .registerComponent(RotationData)
    .registerComponent(VelocityData)
    .registerComponent(MassData)
    .registerComponent(LifeTimeData)
    .registerComponent(ConstraintData);

  // Clear canvas system.
  world.registerSystem(ClearCanvasSystem, {
    mainCanvas: mainCanvas,
  });

  // Register Game Play Systems.
  world
    .registerSystem(DraggableSystem, {
      mainCanvas: mainCanvas,
      rotationSlider: rotationSlider,
    })
    .registerSystem(VelocityMoveSystem)
    .registerSystem(FanPhysicsSystem)
    .registerSystem(GravitySystem)
    .registerSystem(ConstraintPhysicsSystem)
    .registerSystem(LifeTimeSystem)
    .registerSystem(SpawnBallSystem);

  // Register Render Systems.
  world
    .registerSystem(FanRenderSystem, {
      mainCanvas: mainCanvas,
    })
    // .registerSystem(BallRenderSystem, {
    //   mainCanvas: mainCanvas,
    // })
    .registerSystem(RopeRenderSystem, {
      mainCanvas: mainCanvas,
    })
    .registerSystem(DebugRenderSystem, {
      mainCanvas: mainCanvas,
    })
    .registerSystem(DraggableHighlightRenderSystem, {
      mainCanvas: mainCanvas,
    });

  // Randomly generate 3 fans.
  for (let i = 0; i < 3; i++) {
    const fanEntity = world.createEntity();
    fanEntity
      .addComponent(FanTag)
      .addComponent(TransformData, {
        position: new Vector2(
          Math.random() * mainCanvas.width,
          Math.random() * mainCanvas.height
        ),
      })
      .addComponent(RotationData, {
        rotation: Math.random() * 2 * Math.PI,
      })
      .addComponent(DraggableTag);
  }

  // Create a rope node.
  create1AnchorRope(world, mainCanvas);
  create2AnchorRope(world, mainCanvas);
  createCloth(world, mainCanvas);
};

const create1AnchorRope = (world: World, mainCanvas: HTMLCanvasElement) => {
  const ropeNodeEntity = world.createEntity();
  ropeNodeEntity
    .addComponent(TransformData, {
      position: new Vector2(mainCanvas.width / 4, mainCanvas.height / 2),
    })
    .addComponent(ConstraintData, {
      neighbors: [],
      constraintForce: 10,
    })
    .addComponent(DraggableTag)
    .addComponent(RopeRootTag)
    .addComponent(RopeTag);

  // Create a rope.
  createRopeHelper(world, ropeNodeEntity, 50, 3, 0.1, 200);
};

const create2AnchorRope = (world: World, mainCanvas: HTMLCanvasElement) => {
  const ropeNodeEntity1 = world.createEntity();
  ropeNodeEntity1
    .addComponent(TransformData, {
      position: new Vector2(mainCanvas.width / 2 - 30, mainCanvas.height / 2),
    })
    .addComponent(ConstraintData, {
      neighbors: [],
      constraintForce: 10,
    })
    .addComponent(DraggableTag)
    .addComponent(RopeRootTag)
    .addComponent(RopeTag);

  let end = createRopeHelper(world, ropeNodeEntity1, 50, 3, 0.1, 200) as Entity;

  const ropeNodeEntity2 = world.createEntity();
  ropeNodeEntity2
    .addComponent(TransformData, {
      position: new Vector2(mainCanvas.width / 2 + 30, mainCanvas.height / 2),
    })
    .addComponent(ConstraintData, {
      neighbors: [new Constraint(end, 3)],
      constraintForce: 10,
    })
    .addComponent(DraggableTag)
    .addComponent(RopeRootTag)
    .addComponent(RopeTag);

  // Append the rope to the end of the previous rope.
  end
    .getMutableComponent(ConstraintData)
    .neighbors.push(new Constraint(ropeNodeEntity2, 3));
};

const createCloth = (world: World, mainCanvas: HTMLCanvasElement) => {
  const center = new Vector2((mainCanvas.width * 3) / 4, mainCanvas.height / 2);

  // Web width.
  const webWidth = 10;
  const webResolution = 10;
  const mass = 1;
  const clothForce = 1000;

  const start = center.sub(
    new Vector2((webWidth * webResolution) / 2, 0).scale(-1)
  );

  const webNodes: Array<Array<Entity>> = new Array();
  for (let i = 0; i < webWidth; i++) {
    const row = new Array();
    for (let j = 0; j < webWidth; j++) {
      row.push(null);
    }
    webNodes.push(row);
  }

  // Create nodes.
  for (let i = 0; i < webWidth; i++) {
    for (let j = 0; j < webWidth; j++) {
      webNodes[i][j] = world.createEntity();
      webNodes[i][j]
        .addComponent(TransformData, {
          position: new Vector2(
            i * webResolution + start.x,
            j * webResolution + start.y
          ),
        })
        .addComponent(ConstraintData, {
          neighbors: [],
          constraintForce: clothForce,
        })
        .addComponent(VelocityData)
        .addComponent(MassData, {
          mass: mass,
        })
        .addComponent(RopeTag)
        .addComponent(BallTag);
    }
  }

  // Add constraint.
  for (let i = 0; i < webWidth; i++) {
    for (let j = 0; j < webWidth; j++) {
      if (i != 0) {
        webNodes[i][j]
          .getComponent(ConstraintData)
          .neighbors.push(new Constraint(webNodes[i - 1][j], webResolution));
      }
      if (i != webWidth - 1) {
        webNodes[i][j]
          .getComponent(ConstraintData)
          .neighbors.push(new Constraint(webNodes[i + 1][j], webResolution));
      }

      if (j != 0) {
        webNodes[i][j]
          .getComponent(ConstraintData)
          .neighbors.push(new Constraint(webNodes[i][j - 1], webResolution));
      }
      if (j != webWidth - 1) {
        webNodes[i][j]
          .getComponent(ConstraintData)
          .neighbors.push(new Constraint(webNodes[i][j + 1], webResolution));
      }
    }
  }

  // Set anchor.
  webNodes[0][0]
    .addComponent(RopeRootTag)
    .addComponent(DraggableTag)
    .removeComponent(MassData);
  webNodes[webWidth - 1][0]
    .addComponent(RopeRootTag)
    .addComponent(DraggableTag)
    .removeComponent(MassData);
};

// Recursively create a rope.
const createRopeHelper = (
  world: World,
  entity: any,
  length: number,
  resolution: number,
  mass: number,
  force: number
) => {
  if (length <= 0) {
    return null;
  }

  // Get the last rope constraint.
  const constraint = entity.getComponent(ConstraintData) as ConstraintData;

  // Create a new rope node.
  const ropeEntity = world.createEntity();
  // Add the constraint to the new rope entity.
  constraint.neighbors.push(new Constraint(ropeEntity, resolution));

  ropeEntity
    .addComponent(TransformData, {
      position: new Vector2(
        entity.getComponent(TransformData).position.x,
        entity.getComponent(TransformData).position.y + resolution
      ),
    })
    .addComponent(ConstraintData, {
      neighbors: [new Constraint(entity, resolution)],
      constraintForce: force,
    })
    .addComponent(VelocityData)
    .addComponent(MassData, {
      mass: mass,
    })
    .addComponent(RopeTag)
    .addComponent(BallTag);

  let res = createRopeHelper(
    world,
    ropeEntity,
    length - 1,
    resolution,
    mass,
    force
  );
  if (res == null) {
    return ropeEntity;
  }
  return res;
};
